import { useAtom } from "jotai";
import { useCallback, useEffect, useRef, useState } from "react";

import { AdapexAds } from "../../../shared/components/adapex-ads.component";
import { useRewardedVideoAd } from "../../../shared/hooks/use-rewarded-video-ad";
import { shufleArray } from "../../../shared/utils";
import {
  matchUpGameAtom,
  currentRoundAtom,
  currentRoundIndexAtom,
} from "../../widget.state";
import { PairType } from "../../widget.types";
import { MatchUpBanner } from "../banner/matchup-banner.component";
import { MatchUpCard } from "../card/card.component";

export function MatchUpRound({ showRoundAds }: { showRoundAds: boolean }) {
  const [matchUpGame] = useAtom(matchUpGameAtom);
  const [currentRoundIndex, setCurrentRoundIndex] = useAtom(
    currentRoundIndexAtom
  );
  const [currentRound, setCurrentRound] = useAtom(
    currentRoundAtom(matchUpGame.rounds[currentRoundIndex].id)
  );
  const [finalizedPairs, setFinalizedPairs] = useState<
    (PairType & { close: boolean; complete: boolean; wrongPair: boolean })[]
  >([]);
  const [openedPairs, setOpenedPairs] = useState<
    (PairType & { index: number })[]
  >([]);
  const [timerRunning, setTimerRunning] = useState<boolean>(false);
  const [pairsCompleted, setPairsCompleted] = useState<number>(0);

  const handleNextRound = useCallback(() => {
    setTimeout(() => {
      if (matchUpGame.rounds[currentRoundIndex + 1]) {
        setCurrentRoundIndex((i) => {
          const nextRoundIndex = i + 1;
          setFinalizedPairs([]);
          setOpenedPairs([]);
          return nextRoundIndex;
        });
      } else {
        document.location.reload();
      }
    }, 700);
  }, [currentRoundIndex, matchUpGame.rounds, setCurrentRoundIndex]);

  const handleAcceptedCpl = useCallback(
    () => {
      setTimeout(() => {
        handleNextRound();
      }, 500);
    },
    [handleNextRound]
  );

  const { triggerAd } = useRewardedVideoAd({
    onAdGranted: handleAcceptedCpl,
    onAdDismissed: handleAcceptedCpl,
    onAdFailed: handleAcceptedCpl,
  });

  const hasTriggeredAd = useRef(false);

  const handleCardClick = (index: number) => {
    const tempFinalizedPairs = finalizedPairs;
    const tempOpenedPairs = openedPairs;
    if (!timerRunning) {
      setTimerRunning(true);
    }
    if (tempOpenedPairs.length < 2) {
      tempFinalizedPairs[index].close = false;
      if (
        tempOpenedPairs.length == 1 &&
        tempFinalizedPairs[tempOpenedPairs[0].index].name !=
          tempFinalizedPairs[index].name
      ) {
        tempFinalizedPairs[tempOpenedPairs[0].index].wrongPair = true;
        tempFinalizedPairs[index].wrongPair = true;
      }
      setFinalizedPairs(tempFinalizedPairs);
      setOpenedPairs(
        tempOpenedPairs.concat([{ ...tempFinalizedPairs[index], index }])
      );
    }
  };

  const handleTimeout = () => {
    const tempCurrentRound = currentRound;
    const tempFinalizedPairs = finalizedPairs.map((pair) => {
      if (!pair.complete) {
        pair.close = false;
      }
      return pair;
    });
    setFinalizedPairs(tempFinalizedPairs);
    setOpenedPairs([]);
    setTimerRunning(false);
    setTimeout(() => {
      tempCurrentRound.isFinalized = true;
      setCurrentRound(tempCurrentRound);
      if (!hasTriggeredAd.current) {
        hasTriggeredAd.current = true;
        triggerAd();
      }
    }, 3000);
  };

  useEffect(() => {
    const tempCurrentRound = currentRound;
    if (!finalizedPairs.length && !currentRound.isFinalized) {
      hasTriggeredAd.current = false;
      setFinalizedPairs(
        shufleArray(currentRound.pairs.concat(currentRound.pairs)).map(
          (pair: PairType) => ({
            name: pair.name!,
            imageLink: pair.imageLink,
            description: pair.description,
            mainIndex: pair.mainIndex!,
            close: true,
            complete: false,
            wrongPair: false,
          })
        )
      );
      setPairsCompleted(0);
      setTimerRunning(false);
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    } else if (
      !tempCurrentRound.isFinalized &&
      pairsCompleted === currentRound.pairs.length
    ) {
      setTimerRunning(false);
      setTimeout(() => {
        tempCurrentRound.isFinalized = true;
        setCurrentRound(tempCurrentRound);
        if (!hasTriggeredAd.current) {
          hasTriggeredAd.current = true;
          triggerAd();
        }
      }, 3000);
    } else if (openedPairs.length == 2) {
      const tempFinalizedPairs = finalizedPairs;
      const pairCorrect = openedPairs[0].name === openedPairs[1].name;
      if (pairCorrect) {
        tempFinalizedPairs[openedPairs[0].index].complete = true;
        tempFinalizedPairs[openedPairs[1].index].complete = true;
        tempCurrentRound.pairs[
          tempFinalizedPairs[openedPairs[0].index].mainIndex!
        ].isMatched = true;
      } else {
        tempFinalizedPairs[openedPairs[0].index].wrongPair = false;
        tempFinalizedPairs[openedPairs[1].index].wrongPair = false;
        tempFinalizedPairs[openedPairs[0].index].close = true;
        tempFinalizedPairs[openedPairs[1].index].close = true;
      }
      setTimeout(
        () => {
          setFinalizedPairs(tempFinalizedPairs);
          setOpenedPairs([]);
          if (pairCorrect) {
            setCurrentRound(tempCurrentRound);
            setPairsCompleted((current) => {
              return current + 1;
            });
          }
        },
        pairCorrect ? 300 : 900
      );
    }
  }, [
    openedPairs,
    currentRound,
    setCurrentRound,
    finalizedPairs,
    pairsCompleted,
    handleAcceptedCpl,
    triggerAd,
  ]);

  return (
    <div className="match-up--round">
      <MatchUpBanner
        key={`${currentRound.id}-banner`}
        pairsNumber={currentRound.pairs.length}
        brand={currentRound.brandName}
        brandLogo={currentRound.media.logoUrl}
        brandDescription={currentRound.brandDescription ?? ""}
        pairsCompleted={pairsCompleted}
        timerRunning={timerRunning}
        handleTimeout={() => handleTimeout()}
      />
      <div
        className={`match-up--round-game ${
          currentRound.pairs.length % 5 === 0
            ? `five-cards-width${
                currentRound.pairs.length === 10 ? " four-cards-height" : ""
              }`
            : currentRound.pairs.length % 3 === 0
            ? `six-cards-width${
                currentRound.pairs.length === 9 ? " three-cards-height" : ""
              }`
            : currentRound.pairs.length === 7
            ? "seven-cards-width"
            : currentRound.pairs.length === 8
            ? "eight-cards-width"
            : ""
        }`}
      >
        {finalizedPairs.map((pair, index) => (
          <MatchUpCard
            key={`${pair.name}-${index}`}
            index={index}
            brandLogo={currentRound.media.logoUrl}
            name={pair.name!}
            imageLink={pair.imageLink}
            description={pair.description}
            onCardClick={() => handleCardClick(index)}
            close={pair.close}
            complete={pair.complete}
            wrongPair={pair.wrongPair}
          />
        ))}
      </div>

      {/* Ads */}
      {showRoundAds ? (
        <div
          key={`round-ad-${currentRoundIndex}`}
          className="arcade--matchup__body__round-ad"
        >
          <div className="desktop">
            {(currentRoundIndex + 1) % 3 === 0 ? (
              <AdapexAds adunit="fkl_728v_1" />
            ) : (currentRoundIndex + 1) % 2 === 0 ? (
              <AdapexAds adunit="fkl_728v_2" />
            ) : (currentRoundIndex + 1) % 1 === 0 ? (
              <AdapexAds adunit="fkl_728v_3" />
            ) : null}
          </div>

          <div
            className={[
              "mobile",
              (currentRoundIndex + 1) % 3 === 0
                ? "fixedHeight"
                : "fixedShortHeight",
            ].join(" ")}
          >
            {(currentRoundIndex + 1) % 3 === 0 ? (
              <AdapexAds adunit="fkl_MOB_300v_1" />
            ) : (currentRoundIndex + 1) % 2 === 0 ? (
              <AdapexAds adunit="fkl_MOB_300v_2" />
            ) : (currentRoundIndex + 1) % 1 === 0 ? (
              <AdapexAds adunit="fkl_MOB_300v_3" />
            ) : null}
          </div>
        </div>
      ) : (
        <div />
      )}
    </div>
  );
}
